import React, { useState, useCallback } from 'react';

interface NewsImageCarouselProps {
  images: string[];
  alt: string;
}

const NewsImageCarousel: React.FC<NewsImageCarouselProps> = ({ images, alt }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const count = images.length;

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % count);
  }, [count]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + count) % count);
  }, [count]);

  if (count === 0) return null;

  return (
    <div className="relative w-full group">
      <div className="relative w-full h-[500px] sm:h-[600px] flex items-center justify-center overflow-hidden">
        {images.map((url, index) => (
          <div
            key={`${url}-${index}`}
            className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 ease-out"
            style={{
              opacity: index === currentIndex ? 1 : 0,
              pointerEvents: index === currentIndex ? 'auto' : 'none',
            }}
          >
            <img
              src={url}
              alt={`${alt} (${index + 1} av ${count})`}
              className="max-w-full max-h-full w-auto h-auto object-contain"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Arrows - only when multiple images */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Forrige bilde"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 text-neon-green border border-neon-green/50 opacity-80 hover:opacity-100 transition-opacity hover:bg-neon-green/20"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Neste bilde"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 text-neon-green border border-neon-green/50 opacity-80 hover:opacity-100 transition-opacity hover:bg-neon-green/20"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </button>

          {/* Indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-neon-green text-sm font-medium border border-neon-green/30">
            {currentIndex + 1}/{count}
          </div>
        </>
      )}
    </div>
  );
};

export default NewsImageCarousel;
